import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Label } from "semantic-ui-react";

import { PinTypeIcon, TextareaEditable, getPinColor } from "components";
import { QuestionResultStatusSelector, SolutionElement } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components";
import { useAppointmentLocation, useCarBrandsQuery } from "modules/AppointmentDetails/hooks";
import { QuestionResultMedia } from "modules/HoldingAppointmentDetails/components/Checks/components/Check/components/QuestionResults/components";

import type { PinModelWithHistory } from "components";
import type { Appointment, Checklist } from "models";
import type { SortedQuestionResult } from "modules/AppointmentDetails/hooks";

type QuestionResultProps = {
  questionResult: SortedQuestionResult;
  checklist?: Checklist;
  appointment: Appointment;
  onOpenPinModal?: (pinWithHistory: PinModelWithHistory) => void;
  connectedQuestionResultId: number | null;
};

export const QuestionResult = ({ questionResult, appointment, onOpenPinModal, connectedQuestionResultId }: QuestionResultProps) => {
  const { t } = useTranslation();

  const [showMedia, setShowMedia] = useState(false);

  const appointmentLocation = useAppointmentLocation();
  const { data: brands = [] } = useCarBrandsQuery();

  if (connectedQuestionResultId && questionResult.id !== connectedQuestionResultId) return null;

  const latestPin = questionResult.pin_history.at(0);
  const mediaCount = (questionResult.images?.filter(image => image.active).length ?? 0) + (questionResult.videos?.filter(video => video.active).length ?? 0);

  useEffect(() => {
    if (mediaCount <= 0) setShowMedia(false);
  }, [mediaCount]);

  const toggleShowMedia = () => setShowMedia(prev => !prev);
  const handlePinClick = () => {
    if (latestPin) onOpenPinModal?.({ ...latestPin, appointment, history: questionResult.pin_history });
  };

  return (
    <div className="QuestionResult">
      <div className="title-container">
        <p>{questionResult.title}</p>
        <div className="title-row">
          <p>
            <strong>{t("v8_remarks", "Remarks")}:</strong>
          </p>
          <TextareaEditable value={questionResult.raw} disabled />
        </div>
        <div className="QuestionResult-actions">
          {latestPin && appointmentLocation && (
            <div className="status-container">
              <div className="QuestionResult-item-status">
                <Label color={getPinColor({ pin: latestPin, vin: appointment.vin_nr, location: appointmentLocation, brands, questionResult })} onClick={handlePinClick}>
                  <PinTypeIcon pinTypeId={latestPin.pin_type_id} />
                </Label>
              </div>
            </div>
          )}
          {(questionResult.customer_approved || questionResult.mechanic_fixed) && (
            <div className="status-container">
              {questionResult.customer_approved && (
                <div className="QuestionResult-item-status">
                  <Icon className="thumbs up green" />
                </div>
              )}
              {questionResult.mechanic_fixed && (
                <div className="QuestionResult-item-status">
                  <Icon className="check green" />
                </div>
              )}
            </div>
          )}
          <SolutionElement questionResult={questionResult} />
          <div className="status-container">
            <QuestionResultStatusSelector questionResult={questionResult} disabled />
            {mediaCount > 0 && (
              <div onClick={toggleShowMedia}>
                <Icon className="images grey pointer" size="small" />
                <p>
                  <small>{mediaCount}</small>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {showMedia && <QuestionResultMedia questionResult={questionResult} appointment={appointment} />}
    </div>
  );
};
